import { useState, useMemo } from "react";
import { useTranslation } from "@/packages/utils";
import { Search, Loader2 } from "lucide-react";
import { SideSheet } from "@/components/ui";
import { useAudits } from "@/hooks/use-audits";
import { useAuditStatusLabels } from "./useAuditStatusLabels";
import type { AuditId } from "@/packages/types";

interface AuditSelectSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  onSelect: (auditId: AuditId) => void;
  /** When false, the sheet stays open after selection. Default: true */
  closeOnSelect?: boolean;
}

export function AuditSelectSheet({
  open,
  onClose,
  title,
  description,
  onSelect,
  closeOnSelect = true,
}: AuditSelectSheetProps) {
  const { t } = useTranslation("audits");
  const statusLabels = useAuditStatusLabels();
  const { audits, isLoading } = useAudits();
  const [searchValue, setSearchValue] = useState("");

  const filtered = useMemo(() => {
    if (!audits) return [];
    const q = searchValue.toLowerCase();
    if (!q) return audits;
    return audits.filter(
      (a) =>
        a.displayName.toLowerCase().includes(q) ||
        a.code.toLowerCase().includes(q),
    );
  }, [audits, searchValue]);

  const handleSelect = (auditId: AuditId) => {
    onSelect(auditId);
    if (closeOnSelect) onClose();
  };

  return (
    <SideSheet open={open} onClose={onClose}>
      <SideSheet.Header title={title} description={description} />
      <SideSheet.Body>
        <div className="relative mb-4 mt-2">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="text"
            placeholder={t("selectSheet.searchPlaceholder")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full rounded-lg border border-border bg-card py-2 pl-8 pr-3 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12 text-muted">
            <Loader2 size={20} className="animate-spin" />
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted text-sm">
            {t("selectSheet.noAuditsFound")}
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <ul className="flex flex-col gap-1">
            {filtered.map((audit) => {
              const statusCfg =
                statusLabels[audit.status] ?? statusLabels.pending;
              const progress =
                audit.progress.total > 0
                  ? Math.round(
                      (audit.progress.completed / audit.progress.total) * 100,
                    )
                  : 0;

              return (
                <li key={audit.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(audit.id)}
                    className="w-full text-left rounded-lg border border-border bg-card px-4 py-3 hover:bg-card-hover transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-sm font-medium text-primary truncate">
                        {audit.displayName}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="rounded-full px-2 py-0.5 text-[11px] font-medium border border-border text-secondary">
                          {audit.code}
                        </span>
                        <span
                          className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                          style={{
                            backgroundColor: statusCfg.badgeBg,
                            color: statusCfg.badgeText,
                          }}
                        >
                          {statusCfg.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
                        <div
                          className="h-full rounded-full bg-accent transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-muted shrink-0">
                        {progress}%
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </SideSheet.Body>
    </SideSheet>
  );
}
